import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IFilter, IQueryParam, ITema } from 'modelos/src';
import { TemasService } from '../../../../../auxiliares/servicios/http/temas.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { fechaCelda } from '../../../../../auxiliares/tabla/celdas';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';

@Component({
  selector: 'app-listado-temas',
  templateUrl: './listado-temas.component.html',
  styleUrl: './listado-temas.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class ListadoTemasComponent implements OnInit {
  private service = inject(TemasService);
  private dialog = inject(DialogService);
  private helper = inject(HelperService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  public temas: ITema[] = [];
  public loading = false;
  // Tabla común (antes mat-table a mano)
  public name = 'ListadoTemasComponent';
  public query: IQueryParam = {};
  public columnas: IColumnas<ITema>[] = [];
  public botonCrear: ICrearBoton = {
    mostrar: true,
    tooltip: 'Crear tema',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  private setColumnas() {
    this.columnas = [
      {
        header: { label: 'Nombre' },
        row: {
          html: (t) =>
            t.nombre || t.descripcion
              ? `<div class="tabla-celda-col">
                  <span class="tabla-principal">${escaparHtml(t.nombre ?? '')}</span>
                  ${t.descripcion ? `<span class="tabla-secundario">${escaparHtml(t.descripcion)}</span>` : ''}
                </div>`
              : '',
          noData: 'Sin nombre',
        },
      },
      // Antes un interruptor en la celda; ahora estado + Activar/Desactivar
      {
        header: { label: 'Estado' },
        row: {
          estado: (t) =>
            t.activa
              ? { texto: 'Activa', tono: 'ok', icono: 'toggle_on' }
              : { texto: 'Inactiva', tono: 'neutro', icono: 'toggle_off' },
        },
      },
      {
        header: { label: 'Prioridad' },
        row: { field: 'prioridad', noData: 'Sin prioridad' },
      },
      {
        header: { label: 'Vigencia' },
        row: { parse: (t) => this.vigencia(t) },
      },
      {
        header: { label: 'Acciones' },
        row: {
          acciones: [
            {
              tipo: 'material',
              icon: 'edit',
              tooltip: 'Editar',
              click: (t) => this.editar(t),
            },
            {
              tipo: 'material',
              icon: 'toggle_on',
              tooltip: 'Activar',
              click: (t) => this.toggleActiva(t),
              oculta: (t) => !!t.activa,
            },
            {
              tipo: 'material',
              icon: 'toggle_off',
              tooltip: 'Desactivar',
              click: (t) => this.toggleActiva(t),
              oculta: (t) => !t.activa,
            },
            {
              tipo: 'material',
              color: 'warn',
              icon: 'delete',
              tooltip: 'Eliminar',
              click: (t) => this.eliminar(t),
            },
          ],
        },
      },
    ];
  }

  public async listar() {
    this.loading = true;
    try {
      const filter: IFilter<ITema> = {};
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-prioridad',
        limit: 200,
      };
      const data = await this.service.getFiltered(query);
      this.temas = data.datos;
    } catch (e) {
      console.error(e);
      this.helper.notifError(e);
    } finally {
      this.loading = false;
    }
  }

  public crear() {
    this.router.navigate(['crear'], { relativeTo: this.route });
  }

  public editar(t: ITema) {
    this.router.navigate(['editar', t._id], { relativeTo: this.route });
  }

  public async toggleActiva(t: ITema) {
    try {
      await this.service.update(t._id!, { activa: !t.activa });
      this.helper.notifSuccess('Estado actualizado');
      await this.listar();
    } catch (e) {
      console.error(e);
      this.helper.notifError(e);
    }
  }

  public async eliminar(t: ITema) {
    const ok = await this.dialog.confirm(
      'Eliminar tema',
      `¿Querés eliminar el tema "${t.nombre}"?`,
    );
    if (!ok) return;
    try {
      await this.service.delete(t._id!);
      this.helper.notifSuccess('Tema eliminado');
      await this.listar();
    } catch (e) {
      console.error(e);
      this.helper.notifError(e);
    }
  }

  public vigencia(t: ITema): string {
    if (t.diasRecurrentes) {
      const { inicio, fin } = t.diasRecurrentes;
      const fmt = (m: number, d: number) =>
        `${String(d).padStart(2, '0')}/${String(m + 1).padStart(2, '0')}`;
      return `Anual ${fmt(inicio.mes, inicio.dia)} → ${fmt(fin.mes, fin.dia)}`;
    }
    if (t.fechaInicio && t.fechaFin) {
      const ini = fechaCelda(t.fechaInicio).slice(0, 10);
      const fin = fechaCelda(t.fechaFin).slice(0, 10);
      return `${ini} → ${fin}`;
    }
    return 'Permanente';
  }
}
